// API configuration and helper functions

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

/**
 * Load notes from API for authenticated users
 */
export async function loadNotesFromAPI(userId) {
  const res = await fetch(`${API_URL}/api/notes`, {
    headers: {
      'Authorization': `Bearer ${userId}`
    }
  });

  if (!res.ok) {
    throw new Error(`Failed to load notes: ${res.status}`);
  }

  const data = await res.json();
  return data.notes || [];
}

/**
 * Save notes to API for authenticated users
 */
export async function saveNotesToAPI(userId, notes) {
  const res = await fetch(`${API_URL}/api/notes/save`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${userId}`
    },
    body: JSON.stringify({ notes })
  });

  if (!res.ok) {
    throw new Error(`Failed to save notes: ${res.status}`);
  }

  return res.json();
}

/**
 * Sync guest notes with user account after login
 */
export async function syncGuestNotes(userId, guestNotes) {
  const res = await fetch(`${API_URL}/api/notes/sync`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${userId}`
    },
    body: JSON.stringify({ guestNotes })
  });

  if (!res.ok) {
    throw new Error(`Failed to sync notes: ${res.status}`);
  }

  return res.json();
}

/**
 * Delete a specific note
 */
export async function deleteNoteFromAPI(userId, noteId) {
  const res = await fetch(`${API_URL}/api/notes/${noteId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${userId}`
    }
  });

  if (!res.ok) {
    throw new Error(`Failed to delete note: ${res.status}`);
  }

  return res.json();
}

/**
 * Check if user is a guest
 */
export function isGuestUser(userId) {
  return userId?.startsWith('guest:');
}

/**
 * Get authorization header for API calls
 */
export function getAuthHeader(userId) {
  // Prefer the JWT NotesStore keeps in localStorage (key 'authToken').
  const token = localStorage.getItem('authToken');
  if (token) {
    return `Bearer ${token}`;
  }
  // No token: guest mode, or a userId already prefixed with user:/guest:
  if (userId?.startsWith('user:') || userId?.startsWith('guest:')) {
    return userId;
  }
  return `user:${userId}`;
}

/**
 * If a request came back unauthorized, the token is gone/invalid server-side
 * (e.g. the 'user:' bypass was removed). Clear the session and force re-login.
 */
export function handleUnauthorized(res) {
  if (res.status === 401) {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userId');
    window.location.reload();
  }
  return res;
}

/**
 * Get presigned URL for uploading a file to S3
 * S3 path will be: {prefix}/{noteId}/{entryId}-{slug_filename}.{ext}
 */
export async function getPresignedUploadUrl(userId, noteId, entryId, filename, fileType) {
  const res = await fetch(`${API_URL}/api/attachments/presigned-upload`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': getAuthHeader(userId)
    },
    body: JSON.stringify({
      noteId: noteId,
      entryId: entryId,
      fileName: filename,
      fileType: fileType
    })
  });

  handleUnauthorized(res);
  if (!res.ok) {
    throw new Error(`Failed to get presigned URL: ${res.status}`);
  }

  return res.json(); // { url: presignedUrl, s3Key: key }
}

/**
 * Upload file directly to S3 using presigned URL
 */
export async function uploadToS3(presignedUrl, file, contentType = file.type) {
  const res = await fetch(presignedUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType
    },
    body: file
  });

  if (!res.ok) {
    throw new Error(`Failed to upload to S3: ${res.status}`);
  }

  return res;
}

/**
 * Get presigned URL for downloading/viewing a file from S3
 */
export async function getPresignedDownloadUrl(userId, s3Key) {
  const params = new URLSearchParams({ s3Key });
  const res = await fetch(`${API_URL}/api/attachments/presigned-download?${params}`, {
    headers: {
      'Authorization': getAuthHeader(userId)
    }
  });

  handleUnauthorized(res);
  if (!res.ok) {
    throw new Error(`Failed to get download URL: ${res.status}`);
  }

  return res.json(); // { url: presignedUrl }
}

/**
 * Delete a file from S3
 */
export async function deleteAttachment(userId, s3Key) {
  const res = await fetch(`${API_URL}/api/attachments/${encodeURIComponent(s3Key)}`, {
    method: 'DELETE',
    headers: {
      'Authorization': getAuthHeader(userId)
    }
  });

  handleUnauthorized(res);
  if (!res.ok) {
    throw new Error(`Failed to delete attachment: ${res.status}`);
  }

  return res.json();
}

export async function updateAttachmentLabel(userId, noteId, entryId, s3Key, displayName) {
  const res = await fetch(`${API_URL}/api/attachments/label`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'Authorization': getAuthHeader(userId) },
    body: JSON.stringify({ noteId, entryId, s3Key, displayName })
  });
  handleUnauthorized(res);
  if (!res.ok) throw new Error(`Failed to update attachment label: ${res.status}`);
  return res.json();
}

// ===== FINANCIAL ENTRIES METADATA =====

/**
 * Search/list financial entries metadata
 * @param {string} userId - User ID for auth
 * @param {Object} options - { q?: string, kind?: 'income'|'expense' }
 * @returns {Promise<Array>} Array of metadata items
 */
export async function searchMetadata(userId, { q, kind } = {}) {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (kind) params.set('kind', kind);

  const res = await fetch(`${API_URL}/api/financial-entries-metadata?${params}`, {
    headers: {
      'Authorization': getAuthHeader(userId)
    }
  });

  handleUnauthorized(res);
  if (!res.ok) {
    throw new Error(`Failed to search metadata: ${res.status}`);
  }

  const data = await res.json();
  return data.metadata || [];
}

/**
 * Create new financial entries metadata
 * @param {string} userId - User ID for auth
 * @param {Object} data - { name: string, kind: 'income'|'expense' }
 * @returns {Promise<Object>} Created metadata
 */
export async function createMetadata(userId, { name, kind }) {
  const res = await fetch(`${API_URL}/api/financial-entries-metadata`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': getAuthHeader(userId)
    },
    body: JSON.stringify({ name, kind })
  });

  handleUnauthorized(res);
  if (!res.ok) {
    throw new Error(`Failed to create metadata: ${res.status}`);
  }

  return res.json();
}

/**
 * Update financial entries metadata (rename or archive)
 * @param {string} userId - User ID for auth
 * @param {string} id - Metadata ID
 * @param {Object} updates - { name?, archivedAt? }
 * @returns {Promise<Object>} Updated metadata
 */
export async function updateMetadataItem(userId, id, updates) {
  const res = await fetch(`${API_URL}/api/financial-entries-metadata/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': getAuthHeader(userId)
    },
    body: JSON.stringify(updates)
  });

  handleUnauthorized(res);
  if (!res.ok) {
    throw new Error(`Failed to update metadata: ${res.status}`);
  }

  return res.json();
}

/**
 * Update entry identity (link to metadata or set custom name)
 * @param {string} userId - User ID for auth
 * @param {string} noteId - Note ID
 * @param {string} entryId - Entry ID
 * @param {Object} identity - Either { metadataId, kind } OR { customName, kind }
 * @returns {Promise<Object>} Updated entry
 */
export async function updateEntryIdentity(userId, noteId, entryId, identity) {
  const res = await fetch(`${API_URL}/api/notes/${noteId}/entries/${entryId}/identity`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': getAuthHeader(userId)
    },
    body: JSON.stringify(identity)
  });

  handleUnauthorized(res);
  if (!res.ok) {
    throw new Error(`Failed to update entry identity: ${res.status}`);
  }

  return res.json();
}

// ===== PAYMENT SOURCES (cash / card instruments) =====

/**
 * List the user's payment sources (cash and cards)
 */
export async function fetchPaymentSources(userId) {
  const res = await fetch(`${API_URL}/api/payment-sources`, {
    headers: { 'Authorization': getAuthHeader(userId) }
  });

  handleUnauthorized(res);
  if (!res.ok) {
    throw new Error(`Failed to fetch payment sources: ${res.status}`);
  }

  return res.json();
}

/**
 * Create the default cash + one-per-card set. Idempotent server-side.
 */
export async function seedPaymentSources(userId) {
  const res = await fetch(`${API_URL}/api/payment-sources/seed`, {
    method: 'POST',
    headers: { 'Authorization': getAuthHeader(userId) }
  });

  handleUnauthorized(res);
  if (!res.ok) {
    throw new Error(`Failed to seed payment sources: ${res.status}`);
  }

  return res.json();
}

// ===== ENTRY SOURCES (income allocation) =====

/**
 * List active entry-source links touching a note (either side)
 */
export async function fetchEntrySources(userId, noteId) {
  const params = new URLSearchParams({ noteId });
  const res = await fetch(`${API_URL}/api/entry-sources?${params}`, {
    headers: { 'Authorization': getAuthHeader(userId) }
  });

  handleUnauthorized(res);
  if (!res.ok) {
    throw new Error(`Failed to fetch entry sources: ${res.status}`);
  }

  return res.json();
}

/**
 * List candidate income entries for the "Pago com" picker
 */
export async function fetchIncomeCandidates(userId, noteId, fromNoteId) {
  const params = new URLSearchParams({ noteId });
  if (fromNoteId) params.set('fromNoteId', fromNoteId);

  const res = await fetch(`${API_URL}/api/entry-sources/income-candidates?${params}`, {
    headers: { 'Authorization': getAuthHeader(userId) }
  });

  handleUnauthorized(res);
  if (!res.ok) {
    throw new Error(`Failed to fetch income candidates: ${res.status}`);
  }

  return res.json();
}

/**
 * Create (or upsert-by-tuple) an entry-source link
 */
export async function createEntrySource(userId, payload) {
  const res = await fetch(`${API_URL}/api/entry-sources`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': getAuthHeader(userId)
    },
    body: JSON.stringify(payload)
  });

  handleUnauthorized(res);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error.error || `Failed to create entry source: ${res.status}`);
  }

  return res.json();
}

/**
 * Update an entry-source link's amount
 */
export async function updateEntrySourceAmount(userId, id, amount) {
  const res = await fetch(`${API_URL}/api/entry-sources/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': getAuthHeader(userId)
    },
    body: JSON.stringify({ amount })
  });

  handleUnauthorized(res);
  if (!res.ok) {
    const error = await res.json().catch(() => ({}));
    throw new Error(error.error || `Failed to update entry source: ${res.status}`);
  }

  return res.json();
}

/**
 * Delete an entry-source link
 */
export async function deleteEntrySource(userId, id) {
  const res = await fetch(`${API_URL}/api/entry-sources/${id}`, {
    method: 'DELETE',
    headers: { 'Authorization': getAuthHeader(userId) }
  });

  handleUnauthorized(res);
  if (!res.ok) {
    throw new Error(`Failed to delete entry source: ${res.status}`);
  }

  return res.json();
}